import React from "react"
import { DollarSign, Package, Clock, ArrowRight } from "lucide-react"
import { useVendorProducts } from "@/hooks/useProducts"
import { useVendorOrders, useVendorAnalytics } from "@/hooks/useOrders"
import { FulfillmentType, OrderStatus } from "@/api/api-client"
import { OrderStatusBadge } from "@/components/orders/OrderStatusBadge"
import { cn } from "@/lib/utils"
import { formatCurrency } from "@/lib/productDisplay"
import { useLanguage } from "@/hooks/useLanguage"
import {
  formatPricePerWeightUnit,
  formatTotalWeight,
  formatUnitsBreakdown,
  getPricePerWeightUnit,
} from "@/lib/productDisplay"

interface VendorOverviewProps {
    onNavigateTab: (tab: string) => void
    onNavigateToProduct: (id: number) => void
    onNavigateToOrder: (id: number) => void
}

const VendorOverview: React.FC<VendorOverviewProps> = ({ onNavigateTab, onNavigateToProduct, onNavigateToOrder }) => {
    const { data: products } = useVendorProducts()
    const { data: orders } = useVendorOrders()
    const { data: analytics } = useVendorAnalytics(30)
    const { t } = useLanguage()
    const text = t.vendorDashboard

    const activeProductsCount = products?.filter((p) => (p.stockQuantity ?? 0) > 0).length ?? 0
    const pendingOrdersCount =
        orders?.filter(
            (o) => o.status !== OrderStatus.OrderComplete && o.status !== OrderStatus.OrderDeclined
        ).length ?? 0
    const totalSales =
        analytics?.totalRevenue ??
        orders
            ?.filter((o) => o.status === OrderStatus.OrderComplete)
            .reduce((acc, o) => acc + (o.totalAmount ?? 0), 0) ??
        0

    const salesGrowth = analytics?.salesGrowthPercent ?? 0
    const salesTrend = salesGrowth >= 0
        ? `+${salesGrowth.toFixed(1)}% vs prev`
        : `${salesGrowth.toFixed(1)}% vs prev`
    const lowStockCount = analytics?.inventoryHealth?.lowStockCount ?? 0
    const lowStockText = `${lowStockCount} ${t.common.items} low on stock`
    const newOrdersToday = analytics?.newOrdersToday ?? 0
    const newOrdersTrend = `+${newOrdersToday} new today`

    const getStockBadgeClass = (qty: number) => {
        if (qty > 10) return "bg-success/15 text-success"
        if (qty > 0) return "bg-warning/15 text-warning"
        return "bg-destructive/15 text-destructive"
    }

    const statusLabels = t.vendorOrders?.statuses as Partial<Record<string, string>> | undefined

    const formatReceivedDate = (order: { createdAt?: Date | string; masterOrder?: { orderDate?: Date | string } }) => {
        const receivedAt = order.createdAt ?? order.masterOrder?.orderDate
        if (!receivedAt) return "—"

        const date = new Date(receivedAt)
        if (Number.isNaN(date.getTime())) return "—"

        return new Intl.DateTimeFormat(undefined, {
            year: "numeric",
            month: "short",
            day: "2-digit",
        }).format(date)
    }

    return (
        <div className="space-y-10">
            {/* Metric Cards Row */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                {/* Total Sales */}
                <button
                    onClick={() => onNavigateTab("performance")}
                    className="group flex flex-col border border-border bg-card p-6 text-left transition-all duration-200 hover:border-accent/40 hover:shadow-md"
                >
                    <div className="mb-4 flex w-full items-center justify-between">
                        <span className="label-industrial">{text?.stats?.totalSales || "Total Sales"}</span>
                        <div className="flex h-8 w-8 items-center justify-center bg-accent/10 text-accent group-hover:bg-accent/20">
                            <DollarSign className="h-4 w-4" />
                        </div>
                    </div>
                    <div className="mb-1 font-display text-3xl tracking-widest text-accent">
                        {formatCurrency(totalSales, { grouped: true })}
                    </div>
                    <p className="text-xs font-bold text-accent">
                        <span className="mr-1">{salesGrowth >= 0 ? "↗" : "↘"}</span> {salesTrend}
                    </p>
                </button>

                {/* Active Products */}
                <button
                    onClick={() => onNavigateTab("inventory")}
                    className="group flex flex-col border border-border bg-card p-6 text-left transition-all duration-200 hover:border-info/40 hover:shadow-md"
                >
                    <div className="mb-4 flex w-full items-center justify-between">
                        <span className="label-industrial">{text?.stats?.activeProducts || "Active Products"}</span>
                        <div className="flex h-8 w-8 items-center justify-center bg-info/10 text-info group-hover:bg-info/20">
                            <Package className="h-4 w-4" />
                        </div>
                    </div>
                    <div className="mb-1 font-display text-3xl tracking-widest text-info">
                        {activeProductsCount}
                    </div>
                    <p className="text-xs font-bold text-info">
                        {lowStockText}
                    </p>
                </button>

                {/* Pending Orders */}
                <button
                    onClick={() => onNavigateTab("orders")}
                    className="group flex flex-col border border-border bg-card p-6 text-left transition-all duration-200 hover:border-warning/40 hover:shadow-md"
                >
                    <div className="mb-4 flex w-full items-center justify-between">
                        <span className="label-industrial">{text?.stats?.pendingOrders || "Pending Orders"}</span>
                        <div className="flex h-8 w-8 items-center justify-center bg-warning/10 text-warning group-hover:bg-warning/20">
                            <Clock className="h-4 w-4" />
                        </div>
                    </div>
                    <div className="mb-1 flex items-baseline gap-2">
                        <div className="font-display text-3xl tracking-widest text-warning">
                            {pendingOrdersCount}
                        </div>
                        {pendingOrdersCount > 0 && (
                            <span className="relative flex h-2 w-2">
                                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-warning opacity-75"></span>
                                <span className="relative inline-flex h-2 w-2 rounded-full bg-warning"></span>
                            </span>
                        )}
                    </div>
                    <p className="text-xs font-bold text-warning">
                        <span className="mr-1">↗</span> {newOrdersTrend}
                    </p>
                </button>
            </div>

            {/* Current Inventory Section */}
            <div>
                <div className="mb-4 flex items-center justify-between border-b border-border pb-3">
                    <h2 className="font-display text-xl tracking-widest text-foreground uppercase">{text?.tabs?.inventory || "CURRENT INVENTORY"}</h2>
                    <button
                        onClick={() => onNavigateTab("inventory")}
                        className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-muted-foreground transition-colors hover:text-foreground"
                    >
                        {t.common.more} <ArrowRight className="h-3 w-3" />
                    </button>
                </div>

                <div className="overflow-x-auto border border-border bg-card">
                    <table className="w-full text-left text-sm">
                        <thead>
                            <tr className="border-b border-border bg-muted/30">
                                <th className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-muted-foreground">{t.orderTracking.product}</th>
                                <th className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-muted-foreground">{t.orderTracking.sku}</th>
                                <th className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-muted-foreground">{t.common.price}</th>
                                <th className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-muted-foreground">{t.common.quantity}</th>
                                <th className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-muted-foreground">{t.common.status}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {products?.slice(0, 5).map((product) => {
                                const hasWeight = !!product.weight && product.weight > 0
                                const stockQty = product.stockQuantity ?? 0
                                return (
                                <tr
                                    key={product.id}
                                    onClick={() => onNavigateToProduct(product.id!)}
                                    className="cursor-pointer border-b border-border/50 transition-colors last:border-0 hover:bg-muted/20"
                                >
                                    <td className="flex items-center gap-3 px-4 py-3 font-medium text-foreground">
                                        <div className="h-8 w-8 flex-shrink-0 overflow-hidden border border-border bg-muted">
                                            {product.imageUrls && product.imageUrls.length > 0 ? (
                                                <img src={product.imageUrls[0]} alt={product.name} className="h-full w-full object-cover" />
                                            ) : (
                                                <div className="flex h-full w-full items-center justify-center bg-muted">
                                                    <Package className="h-4 w-4 text-muted-foreground/40" />
                                                </div>
                                            )}
                                        </div>
                                        <span className="block max-w-[200px] truncate" title={product.name}>{product.name}</span>
                                    </td>
                                    <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{product.sku || "-"}</td>
                                    <td className="px-4 py-3 font-mono font-bold text-foreground">
                                        {hasWeight ? (
                                            <>
                                                {formatPricePerWeightUnit(getPricePerWeightUnit(product), product.weightUnit, { isTaxable: !!product.isTaxable })}
                                                <span className="block text-[11px] font-sans font-semibold uppercase tracking-wider text-muted-foreground">
                                                    {product.weight}{product.weightUnit}/{t.units.perUnit}
                                                </span>
                                            </>
                                        ) : (
                                            <>${product.price?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}<span className="font-sans text-xs font-normal text-muted-foreground">/{product.weightUnit || "kg"}</span></>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 font-mono text-foreground">
                                        <div>{stockQty} <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{stockQty === 1 ? t.units.unit : t.units.units}</span></div>
                                        {hasWeight && (
                                            <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                                                {formatTotalWeight(stockQty, product.weight, product.weightUnit, t.units.total)}
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-4 py-3">
                                        <span className={cn(
                                            "inline-flex px-2 py-0.5 text-xs font-bold uppercase tracking-wide",
                                            getStockBadgeClass(product.stockQuantity ?? 0)
                                        )}>
                                            {(product.stockQuantity ?? 0) > 10 ? t.productDetail.inStock : (product.stockQuantity ?? 0) > 0 ? "Low Stock" : t.productDetail.outOfStock}
                                        </span>
                                    </td>
                                </tr>
                                )
                            })}
                            {(!products || products.length === 0) && (
                                <tr>
                                    <td colSpan={5} className="px-4 py-8 text-center text-sm text-muted-foreground">
                                        {text?.stats?.noInventoryFound || "No inventory items found."}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Recent Orders Section */}
            <div>
                <div className="mb-4 flex items-center justify-between border-b border-border pb-3">
                    <h2 className="font-display text-xl tracking-widest text-foreground uppercase">{text?.tabs?.orders || "RECENT ORDERS"}</h2>
                    <button
                        onClick={() => onNavigateTab("orders")}
                        className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-muted-foreground transition-colors hover:text-foreground"
                    >
                        {t.common.more} <ArrowRight className="h-3 w-3" />
                    </button>
                </div>

                <div className="overflow-x-auto border border-border bg-card">
                    <table className="w-full text-left text-sm">
                        <thead>
                            <tr className="border-b border-border bg-muted/30">
                                <th className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-muted-foreground">{t.adminDashboard.order} ID</th>
                                <th className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-muted-foreground">{t.adminDashboard.received || "Received"}</th>
                                <th className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-muted-foreground">{t.common.customer}</th>
                                <th className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-muted-foreground">{t.common.items}</th>
                                <th className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-muted-foreground">{t.common.total}</th>
                                <th className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-muted-foreground">{t.common.status}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {orders?.slice(0, 5).map((order) => {
                                const items = order.items ?? []
                                const totalUnits = items.reduce((sum, item) => sum + (item.quantity || 0), 0)
                                const totalWeight = items.reduce((sum, item) => {
                                    const w = (item as any).weight ?? 0
                                    return sum + w * (item.quantity || 0)
                                }, 0)
                                const weightUnit = (items.find((i: any) => i.weight) as any)?.weightUnit || "kg"
                                return (
                                <tr
                                    key={order.id}
                                    onClick={() => onNavigateToOrder(order.id!)}
                                    className="cursor-pointer border-b border-border/50 transition-colors last:border-0 hover:bg-muted/20"
                                >
                                    <td className="px-4 py-3 font-mono text-xs font-medium text-foreground">#{order.masterOrder?.id ?? order.id}</td>
                                    <td className="px-4 py-3 font-mono text-xs text-muted-foreground whitespace-nowrap">{formatReceivedDate(order)}</td>
                                    <td className="px-4 py-3 text-muted-foreground">{order.masterOrder?.buyer?.email ?? "—"}</td>
                                    <td className="px-4 py-3 font-mono text-foreground">
                                        <div>{totalUnits} <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{totalUnits === 1 ? t.units.unit : t.units.units}</span></div>
                                        {totalWeight > 0 && (
                                            <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                                                {(totalWeight % 1 === 0 ? totalWeight.toString() : totalWeight.toFixed(2))} {weightUnit} {t.units.total}
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 font-mono font-bold text-foreground">
                                        {formatCurrency(order.totalAmount, { grouped: true })}
                                    </td>
                                    <td className="px-4 py-3">
                                        <OrderStatusBadge
                                            status={
                                                (order as any).fulfillmentType === FulfillmentType.Pickup &&
                                                order.status === OrderStatus.ShippingQuoted
                                                    ? OrderStatus.StockVerified
                                                    : (order.status as OrderStatus)
                                            }
                                            labels={statusLabels}
                                        />
                                    </td>
                                </tr>
                                )
                            })}
                            {(!orders || orders.length === 0) && (
                                <tr>
                                    <td colSpan={6} className="px-4 py-8 text-center text-sm text-muted-foreground">
                                        {t.order.notFound || "No recent orders found."}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    )
}

export default VendorOverview

